// @flow
import React from 'react';
import { get, kebabCase } from 'lodash';
import classNames from 'classnames';
import { type FieldProps } from 'formik';

import { getValidationStatus } from 'Components/frameworks/formik/inputsUtils';

import { ImageValidationStatus } from 'Components/frameworks/image-validation-status/ImageValidationStatus';

import { ErrorNote } from './ErrorNote.jsx';
import { Label } from './Label.jsx';

type Props = {
  dataTestId?: string,
  hideErrorMessage: boolean,
  isDisabled?: boolean,
  isRequired?: boolean,
  label?: string,
  placeholder?: string,
  type: string,
  customClasses?: string,
  customId?: string,
  labelAdditionalInfo?: Node,
  ...FieldProps,
};

export const Input = ({
  dataTestId,
  field,
  form,
  hideErrorMessage,
  isDisabled = false,
  isRequired = false,
  label,
  placeholder,
  type,
  customClasses = '',
  customId,
  labelAdditionalInfo,
  ...props
}: Props) => {
  // Input value defaults to empty string
  const value = get(form.values, field.name, '');
  const { isDefaultState, isErrored, isValidated } = getValidationStatus(
    { ...field, value },
    form,
    isDisabled,
  );
  const errorNoteId = `error-${kebabCase(field.name)}`;
  const inputStyle = customClasses
    ? customClasses
    : classNames(
        'border leading-normal text-md sm:text-base outline-none px-3 py-2 rounded-tiny text-gray-darker transition-all w-full',
        {
          'border-gray-medium focus:border-blue': isDefaultState,
          'border-emerald pr-6': isValidated,
          'border-red formik-errored pr-6': isErrored,
          'border-gray-lighter bg-gray-lighter cursor-not-allowed': isDisabled,
        },
      );
  return (
    <React.Fragment>
      {!!label && (
        <Label
          htmlFor={customId || field.name}
          isDisabled={isDisabled}
          isRequired={isRequired}
          label={label}
          labelAdditionalInfo={labelAdditionalInfo}
          isErrored={isErrored}
        />
      )}
      <div className="flex items-center">
        <input
          aria-describedby={errorNoteId}
          aria-invalid={isErrored}
          aria-required={isRequired}
          autoComplete="off"
          className={inputStyle}
          data-testid={dataTestId}
          disabled={isDisabled}
          id={customId ? customId : field.name}
          type={type}
          {...(placeholder ? { placeholder } : null)}
          {...field}
          // default to '' for prevent react warning on controlled component
          value={value || ''}
          {...props}
        />
        {!isDisabled && (
          <ImageValidationStatus
            isErrored={isErrored}
            isValidated={isValidated}
            classNames="-m-5"
          />
        )}
      </div>
      {!hideErrorMessage && (
        <ErrorNote
          isErrored={isErrored}
          errorMessage={form.errors[field.name]}
          errorNoteId={errorNoteId}
        />
      )}
    </React.Fragment>
  );
};
